/**
 * Greeks Cache Service (Client-side version)
 * Browser-safe version that uses API endpoints for database operations
 */

import type { GreeksData } from "@/services/market-data-streamer";
import { symbolMappingService } from "@/services/symbol-mapping-service";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const readString = (record: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }
  }
  return undefined;
};

const readNumber = (record: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
};

export interface CachedGreeks {
  symbol: string;
  streamerSymbol: string;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
  timestamp: number;
  updateCount: number;
  lastValidUpdate: number;
  confidence: number;
}

export class GreeksCacheServiceClient {
  private cache = new Map<string, CachedGreeks>();
  private pendingSaves = new Map<string, CachedGreeks>();
  private saveInterval: NodeJS.Timeout | null = null;
  private readonly BATCH_SAVE_INTERVAL = 30000; // 30 seconds
  private hasLoggedLoadError = false;
  private lastDbErrorTime: number | null = null;
  private history = new Map<string, GreeksData[]>();
  private maxHistorySize = 10;

  constructor() {
    if (typeof window !== "undefined") {
      // Start batch save timer
      this.saveInterval = setInterval(() => {
        this.batchSaveToDatabase();
      }, this.BATCH_SAVE_INTERVAL);

      // Load initial data from database. Skipped on /glance: the
      // secret-link read-only page has no broker session, so this call can
      // only 401 (console noise) and glance never consumes client greeks.
      if (!window.location.pathname.startsWith("/glance")) {
        this.loadFromDatabase();
      }
    }
  }

  /**
   * Get Greeks for a symbol (checks cache first, then database)
   */
  async getGreeks(positionSymbol: string): Promise<CachedGreeks | null> {
    // Check cache first
    const cached = this.cache.get(positionSymbol);
    if (cached) return cached;

    // Try to load from database
    try {
      const response = await fetch(
        `/api/market-data/greeks?symbol=${positionSymbol}`,
      );
      if (response.ok) {
        const { data } = await response.json();
        if (data) {
          // Convert database format to cache format
          const greeks: CachedGreeks = {
            symbol: data.symbol,
            streamerSymbol: data.streamer_symbol,
            delta: data.delta,
            gamma: data.gamma,
            theta: data.theta,
            vega: data.vega,
            rho: data.rho,
            timestamp: data.timestamp,
            updateCount: data.update_count,
            lastValidUpdate: data.last_valid_update,
            confidence: data.confidence,
          };
          this.cache.set(positionSymbol, greeks);
          return greeks;
        }
      }
    } catch {
      // Failed to load Greeks from database
    }

    return null;
  }

  /**
   * Get Greeks for multiple symbols using a single batch request
   */
  async getGreeksBatch(
    positionSymbols: string[],
  ): Promise<Map<string, GreeksData>> {
    const result = new Map<string, GreeksData>();
    const symbolsToFetch: string[] = [];

    // First, check the local in-memory cache for existing data
    for (const positionSymbol of positionSymbols) {
      // Convert position symbol to streamer symbol if needed
      let streamerSymbol = positionSymbol;
      if (positionSymbol.includes(" ") && !positionSymbol.startsWith(".")) {
        const mappedSymbol =
          symbolMappingService.getStreamerSymbol(positionSymbol);
        if (mappedSymbol) {
          streamerSymbol = mappedSymbol;
        }
      }

      const cached = this.cache.get(streamerSymbol);
      if (cached) {
        // Return with original position symbol as key
        result.set(positionSymbol, {
          eventType: "snapshot",
          eventSymbol: positionSymbol,
          timestamp: Date.now(),
          delta: cached.delta,
          gamma: cached.gamma,
          theta: cached.theta,
          vega: cached.vega,
          rho: cached.rho,
        });
      } else {
        // Add the original position symbol to fetch list (API expects position symbols)
        symbolsToFetch.push(positionSymbol);
      }
    }

    // If there are symbols not in the memory cache, fetch them from the backend in a single batch
    if (symbolsToFetch.length > 0) {
      try {
        const response = await fetch(`/api/market-data/greeks-batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: 'include' as RequestCredentials,
          body: JSON.stringify({ symbols: symbolsToFetch }),
        });

        if (response.ok) {
          const { data } = await response.json();
          if (data && Array.isArray(data)) {
            for (const dbGreeks of data) {
              // Update local in-memory cache
              const cachedGreeks: CachedGreeks = {
                symbol: dbGreeks.position_symbol,
                streamerSymbol: dbGreeks.streamer_symbol,
                delta: dbGreeks.delta,
                gamma: dbGreeks.gamma,
                theta: dbGreeks.theta,
                vega: dbGreeks.vega,
                rho: dbGreeks.rho,
                timestamp: dbGreeks.timestamp,
                updateCount: dbGreeks.update_count || 1,
                lastValidUpdate:
                  dbGreeks.last_valid_update || dbGreeks.timestamp,
                confidence: dbGreeks.confidence || 1,
              };
              // Cache by streamer symbol for consistency
              this.cache.set(dbGreeks.streamer_symbol, cachedGreeks);

              // Add to result map with position symbol as key
              result.set(dbGreeks.position_symbol, {
                eventType: "snapshot",
                eventSymbol: dbGreeks.position_symbol,
                timestamp: Date.now(),
                delta: dbGreeks.delta,
                gamma: dbGreeks.gamma,
                theta: dbGreeks.theta,
                vega: dbGreeks.vega,
                rho: dbGreeks.rho,
              });
            }
          }
        }
    } catch {
      // Failed to load Greeks batch from database
    }
    }

    return result;
  }

  /**
   * Update cache and mark for database save
   */
  updateCache(symbol: string, greeks: CachedGreeks): void {
    this.cache.set(symbol, greeks);
    this.pendingSaves.set(symbol, greeks);
  }

  /**
   * Process incoming Greeks update (from the full service)
   */
  processGreeksUpdate(greeks: CachedGreeks): void {
    this.updateCache(greeks.symbol, greeks);
  }

  /**
   * Batch save pending Greeks to database
   */
  private async batchSaveToDatabase(): Promise<void> {
    if (this.pendingSaves.size === 0) return;

    const greeksList = Array.from(this.pendingSaves.values());
    this.pendingSaves.clear();

    try {
      const response = await fetch("/api/market-data/greeks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include' as RequestCredentials,
        body: JSON.stringify({
          action: "saveBatch",
          data: greeksList,
        }),
      });

      if (!response.ok) {
        // Failed to save Greeks batch
      }
    } catch {
      // Error saving Greeks batch
    }
  }

  /**
   * Load Greeks from database on startup
   */
  private async loadFromDatabase(): Promise<void> {
    try {
      const response = await fetch("/api/greeks/cache", {
        credentials: 'include' as RequestCredentials,
      });
      if (response.ok) {
        const { data } = await response.json();

        // Populate cache with database data
        if (Array.isArray(data)) {
          data.forEach((row) => {
            if (!isRecord(row)) return;

            const symbol = readString(row, ["symbol", "position_symbol"]) ?? "";
            const streamerSymbol =
              readString(row, [
                "streamerSymbol",
                "streamer_symbol",
                "symbol",
                "position_symbol",
              ]) ?? symbol;

            const timestamp = readNumber(row, ["timestamp"]) ?? Date.now();
            const lastValidUpdate =
              readNumber(row, ["lastValidUpdate", "last_valid_update"]) ??
              timestamp;
            const updateCount =
              readNumber(row, ["updateCount", "update_count"]) ?? 1;

            const greeks: CachedGreeks = {
              symbol,
              streamerSymbol,
              delta: readNumber(row, ["delta"]) ?? 0,
              gamma: readNumber(row, ["gamma"]) ?? 0,
              theta: readNumber(row, ["theta"]) ?? 0,
              vega: readNumber(row, ["vega"]) ?? 0,
              rho: readNumber(row, ["rho"]) ?? 0,
              timestamp,
              updateCount: Math.max(1, Math.floor(updateCount)),
              lastValidUpdate,
              confidence: readNumber(row, ["confidence"]) ?? 1,
            };
            // Cache by streamer symbol
            const cacheKey = greeks.streamerSymbol.startsWith(".")
              ? greeks.streamerSymbol
              : greeks.symbol;
            this.cache.set(cacheKey, greeks);
          });

          // Loaded Greeks from database
        }
      }
    } catch {
      // Failed to load Greeks from database
    }
  }

  /**
   * Force save all pending data
   */
  async forceSave(): Promise<void> {
    await this.batchSaveToDatabase();
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    this.pendingSaves.clear();
  }

  /**
   * Clean up on unmount
   */
  destroy(): void {
    if (this.saveInterval) {
      clearInterval(this.saveInterval);
      this.saveInterval = null;
    }
    // Save any pending data
    this.batchSaveToDatabase();
  }
}

// Singleton instance for client-side use
export const greeksCacheServiceClient = new GreeksCacheServiceClient();
